import React, { useState, useEffect } from 'react';
import { rslService } from '../../services/api';

interface SafeguardingPanelProps {
    isOpen: boolean;
    onClose: () => void;
    tenant: any;
}

const VULN_STAGES = [
    { id: 'arrears_contact', label: 'First Arrears Contact', description: 'Protocol para 2.1 — first contact after arrears arise' },
    { id: 'pre_notice', label: 'Pre-Notice', description: 'Protocol para 3.2 — before any notice is served' },
];

const VULN_OUTCOMES = [
    { id: 'NO_VULNERABILITY', label: 'No vulnerability identified' },
    { id: 'VULNERABILITY_IDENTIFIED', label: 'Vulnerability identified' },
    { id: 'LACKS_CAPACITY', label: 'Lacks capacity — refer to capacity screen' },
];

const VULN_TYPES = [
    'Mental health condition', 'Physical disability / mobility', 'Learning disability / cognitive',
    'Sensory impairment', 'Age-related frailty', 'Pregnancy / recent birth', 'Caring responsibilities',
    'Care leaver', 'Domestic abuse survivor', 'Drug / alcohol dependency', 'Severe financial hardship',
    'Language / literacy barriers', 'Recently bereaved', 'Other',
];

const PROTECTED_CHARACTERISTICS = [
    'Age', 'Disability', 'Gender reassignment', 'Marriage / civil partnership',
    'Pregnancy / maternity', 'Race', 'Religion / belief', 'Sex', 'Sexual orientation',
];

const COMMUNICATION_NEEDS = [
    'Large print', 'Braille', 'Easy read', 'Audio format', 'Interpreter (spoken)', 'BSL interpreter',
    'Written only', 'Advocate / support worker presence', 'Extra time to respond',
];

const CARE_NEEDS = [
    'Personal care assistance', 'Mobility equipment / adaptations', 'Medication support',
    'Mental health support', 'Meal / nutrition support', 'No care needs identified',
];

const SUPPORT_AGENCIES = [
    'Adult Social Care', 'CMHT (mental health)', 'Floating support', 'Shelter', 'Citizens Advice',
    'Drug / alcohol service', 'Probation / offender manager', 'None involved',
];

const CAPACITY_OUTCOMES = [
    { id: 'CAPACITY_PRESUMED', label: 'Capacity presumed (MCA principle 1)', style: 'green' },
    { id: 'CAPACITY_DOUBTFUL', label: 'Capacity doubtful — refer for formal assessment', style: 'amber' },
    { id: 'LACKS_CAPACITY', label: 'Lacks capacity — open CPR 21 litigation-friend pathway', style: 'red' },
];

const MCA_STAGES = [
    { key: 'mcaUnderstand', label: 'Understand', help: 'Understand the relevant information about the decision' },
    { key: 'mcaRetain', label: 'Retain', help: 'Retain the information long enough to make the decision' },
    { key: 'mcaWeigh', label: 'Weigh', help: 'Weigh the information as part of the decision-making process' },
    { key: 'mcaCommunicate', label: 'Communicate', help: 'Communicate the decision (any means)' },
];

const OUTCOME_STYLES: Record<string, string> = {
    green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-rose-50 text-rose-800 border-rose-200',
};

export default function SafeguardingPanel({ isOpen, onClose, tenant }: SafeguardingPanelProps) {
    const [tab, setTab] = useState<'profile' | 'vuln' | 'capacity' | 'adj' | 'care' | 'protocol' | 'bundle' | 'sustain'>('profile');
    const [profile, setProfile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState<string | null>(null);

    // Vulnerability form state
    const [vStage, setVStage] = useState('arrears_contact');
    const [vOutcome, setVOutcome] = useState<string | null>(null);
    const [vTypes, setVTypes] = useState<string[]>([]);
    const [vProtected, setVProtected] = useState<string[]>([]);
    const [vCare, setVCare] = useState<string[]>([]);
    const [vComms, setVComms] = useState<string[]>([]);
    const [vAgencies, setVAgencies] = useState<string[]>([]);
    const [vNotes, setVNotes] = useState('');

    // Capacity form state
    const [cOutcome, setCOutcome] = useState<string | null>(null);
    const [cStages, setCStages] = useState<Record<string, { able: boolean | null; notes: string }>>({
        mcaUnderstand: { able: null, notes: '' },
        mcaRetain: { able: null, notes: '' },
        mcaWeigh: { able: null, notes: '' },
        mcaCommunicate: { able: null, notes: '' },
    });
    const [cNotes, setCNotes] = useState('');

    // Reasonable adjustments form state (US-053)
    const [adjList, setAdjList] = useState<any[]>([]);
    const [adjLoaded, setAdjLoaded] = useState(false);
    const [aType, setAType] = useState('communication');
    const [aDetail, setADetail] = useState('');
    const [aAuthorisedBy, setAAuthorisedBy] = useState('');
    const [aAppliedFrom, setAAppliedFrom] = useState('');
    const [aReviewDate, setAReviewDate] = useState('');
    const [aNotes, setANotes] = useState('');

    // Care Act referral form state (US-054)
    const [careList, setCareList] = useState<any[]>([]);
    const [careLoaded, setCareLoaded] = useState(false);
    const [crReason, setCrReason] = useState('');
    const [crNeeds, setCrNeeds] = useState('');
    const [crType, setCrType] = useState('community_care');
    const [crConsent, setCrConsent] = useState(false);
    const [crOfficer, setCrOfficer] = useState('');
    const [crNotes, setCrNotes] = useState('');
    const [crOutcomeId, setCrOutcomeId] = useState<string | null>(null);
    const [crOutcome, setCrOutcome] = useState('');
    const [crOutcomeDetails, setCrOutcomeDetails] = useState('');

    // Protocol Part 3 schedule state (US-055 / T-047-005)
    const [compliance, setCompliance] = useState<any>(null);
    const [ptclList, setPtclList] = useState<any[]>([]);
    const [ptclLoaded, setPtclLoaded] = useState(false);
    const [ptclGround, setPtclGround] = useState('GROUND_8');
    const [ptclInviteDate, setPtclInviteDate] = useState('');
    const [ptclInviteMethod, setPtclInviteMethod] = useState('post');
    const [ptclInviteOfficer, setPtclInviteOfficer] = useState('');
    const [ptclReps, setPtclReps] = useState('');
    const [ptclConsiderDate, setPtclConsiderDate] = useState('');
    const [ptclConsiderOfficer, setPtclConsiderOfficer] = useState('');
    const [ptclConsiderOutcome, setPtclConsiderOutcome] = useState('PROCEEDED');
    const [ptclConsiderSummary, setPtclConsiderSummary] = useState('');
    const [ptclReasons, setPtclReasons] = useState('');

    // Evidence bundle state (US-055 / T-047-006)
    const [bundleList, setBundleList] = useState<any[]>([]);
    const [bundleLoaded, setBundleLoaded] = useState(false);
    const [bundleGenerating, setBundleGenerating] = useState(false);

    // US-057 / F-048: Tenancy sustainment state
    const [svcList, setSvcList] = useState<any[]>([]);
    const [svcLoaded, setSvcLoaded] = useState(false);
    const [svcName, setSvcName] = useState('');
    const [svcType, setSvcType] = useState('floating_support');
    const [svcContact, setSvcContact] = useState('');
    const [svcCriteria, setSvcCriteria] = useState('');
    const [svcSla, setSvcSla] = useState(10);
    const [refList, setRefList] = useState<any[]>([]);
    const [refLoaded, setRefLoaded] = useState(false);
    const [refServiceId, setRefServiceId] = useState('');
    const [refReason, setRefReason] = useState('');
    const [refStatusTarget, setRefStatusTarget] = useState<{ id: string; to: string } | null>(null);
    const [refOutcomeTarget, setRefOutcomeTarget] = useState<{ id: string; outcome: string; details: string } | null>(null);
    const [refOutcomeDetails, setRefOutcomeDetails] = useState('');
    const [agrList, setAgrList] = useState<any[]>([]);
    const [agrLoaded, setAgrLoaded] = useState(false);
    const [agrTemplate, setAgrTemplate] = useState('HOSPITAL_DISCHARGE');
    const [agrGenerating, setAgrGenerating] = useState(false);
    const [altList, setAltList] = useState<any[]>([]);
    const [altLoaded, setAltLoaded] = useState(false);
    const [altScanning, setAltScanning] = useState(false);
    const [altActionTarget, setAltActionTarget] = useState<{ id: string; status: string; notes: string } | null>(null);
    const [altActionNotes, setAltActionNotes] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setTab('profile');
            setProfile(null);
            setError(null);
            setSavedMsg(null);
            setVStage('arrears_contact');
            setVOutcome(null);
            setVTypes([]);
            setVProtected([]);
            setVCare([]);
            setVComms([]);
            setVAgencies([]);
            setVNotes('');
            setCOutcome(null);
            setCStages({
                mcaUnderstand: { able: null, notes: '' },
                mcaRetain: { able: null, notes: '' },
                mcaWeigh: { able: null, notes: '' },
                mcaCommunicate: { able: null, notes: '' },
            });
            setCNotes('');
            setAdjList([]);
            setAdjLoaded(false);
            setAType('communication');
            setADetail('');
            setAAuthorisedBy('');
            setAAppliedFrom('');
            setAReviewDate('');
            setANotes('');
            setCareList([]);
            setCareLoaded(false);
            setCrReason('');
            setCrNeeds('');
            setCrType('community_care');
            setCrConsent(false);
            setCrOfficer('');
            setCrNotes('');
            setCrOutcomeId(null);
            setCrOutcome('');
            setCrOutcomeDetails('');
            setCompliance(null);
            setPtclList([]);
            setPtclLoaded(false);
            setPtclGround('GROUND_8');
            setPtclInviteDate('');
            setPtclInviteMethod('post');
            setPtclInviteOfficer('');
            setPtclReps('');
            setPtclConsiderDate('');
            setPtclConsiderOfficer('');
            setPtclConsiderOutcome('PROCEEDED');
            setPtclConsiderSummary('');
            setPtclReasons('');
            setBundleList([]);
            setBundleLoaded(false);
            setSvcList([]);
            setSvcLoaded(false);
            setSvcName('');
            setSvcType('floating_support');
            setSvcContact('');
            setSvcCriteria('');
            setSvcSla(10);
            setRefList([]);
            setRefLoaded(false);
            setRefServiceId('');
            setRefReason('');
            setRefStatusTarget(null);
            setRefOutcomeTarget(null);
            setRefOutcomeDetails('');
            setAgrList([]);
            setAgrLoaded(false);
            setAgrTemplate('HOSPITAL_DISCHARGE');
            setAltList([]);
            setAltLoaded(false);
            setAltActionTarget(null);
            setAltActionNotes('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !tenant?.id) return;
        loadProfile();
    }, [isOpen, tenant?.id]);

    const loadProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await rslService.getSafeguardingProfile(tenant.id);
            setProfile(res.data?.data || {});
            try {
                const c = await rslService.getProtocolCompliance(tenant.id);
                setCompliance(c.data?.data || null);
            } catch {
                setCompliance(null);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load safeguarding profile');
        } finally {
            setLoading(false);
        }
    };

    const toggleIn = (arr: string[], setArr: (v: string[]) => void, value: string) => {
        setArr(arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value]);
    };

    const handleSaveVulnerability = async () => {
        if (!vOutcome) {
            setError('Please select an assessment outcome.');
            return;
        }
        if (vOutcome === 'VULNERABILITY_IDENTIFIED' && vTypes.length === 0) {
            setError('Please select at least one vulnerability type when vulnerability is identified.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.createVulnerabilityAssessment(tenant.id, {
                stage: vStage,
                outcome: vOutcome,
                mentalCapacityIndicators: cStages.mcaUnderstand.able !== null
                    ? { flagged: !cStages.mcaUnderstand.able }
                    : undefined,
                protectedCharacteristics: vProtected,
                careNeeds: vCare,
                communicationNeeds: vComms,
                supportAgencies: vAgencies,
                vulnerabilityTypes: vTypes,
                notes: vNotes,
            });
            setSavedMsg('Vulnerability assessment recorded to Evidence Safe.');
            await loadProfile();
            setTab('profile');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save vulnerability assessment.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCapacity = async () => {
        if (!cOutcome) {
            setError('Please select a capacity outcome.');
            return;
        }
        for (const stage of MCA_STAGES) {
            if (cStages[stage.key].able === null) {
                setError(`Please complete the "${stage.label}" stage of the MCA test.`);
                return;
            }
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            const payload: any = {
                outcome: cOutcome,
                mcaUnderstand: cStages.mcaUnderstand,
                mcaRetain: cStages.mcaRetain,
                mcaWeigh: cStages.mcaWeigh,
                mcaCommunicate: cStages.mcaCommunicate,
                notes: cNotes,
            };
            await rslService.createCapacityScreen(tenant.id, payload);
            setSavedMsg(cOutcome === 'LACKS_CAPACITY'
                ? 'Capacity screen recorded. CPR 21 litigation-friend pathway is now open — refer before court action.'
                : 'Capacity screen recorded to Evidence Safe.');
            await loadProfile();
            setTab('profile');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save capacity screen.');
        } finally {
            setSaving(false);
        }
    };

    const loadAdjustments = async () => {
        try {
            const res = await rslService.listAdjustments(tenant.id);
            setAdjList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load adjustments');
        } finally {
            setAdjLoaded(true);
        }
    };

    const loadCareReferrals = async () => {
        try {
            const res = await rslService.listCareReferrals(tenant.id);
            setCareList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load care referrals');
        } finally {
            setCareLoaded(true);
        }
    };

    const handleSaveAdjustment = async () => {
        if (!aDetail.trim()) {
            setError('Please describe the reasonable adjustment.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.createAdjustment(tenant.id, {
                adjustmentType: aType,
                detail: aDetail,
                authorisedBy: aAuthorisedBy || undefined,
                appliedFrom: aAppliedFrom || undefined,
                reviewDate: aReviewDate || undefined,
                notes: aNotes || undefined,
            });
            setSavedMsg('Reasonable adjustment recorded to the Evidence Safe.');
            await Promise.all([loadProfile(), loadAdjustments()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save reasonable adjustment.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCareReferral = async () => {
        if (!crReason.trim()) {
            setError('Please state the reason for the Care Act referral.');
            return;
        }
        if (!crConsent) {
            setError('Consent to share information with Adult Social Care is required for the referral.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.createCareReferral(tenant.id, {
                referralReason: crReason,
                careNeedsSummary: crNeeds,
                requestedAssessmentType: crType,
                consentGiven: crConsent,
                referringOfficer: crOfficer || undefined,
                notes: crNotes || undefined,
            });
            setSavedMsg('Care Act referral submitted — 28-day SLA tracking started.');
            await Promise.all([loadProfile(), loadCareReferrals()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to submit Care Act referral.');
        } finally {
            setSaving(false);
        }
    };

    const handleCareOutcome = async (id: string) => {
        if (!crOutcome) {
            setError('Please select the assessment outcome.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.updateCareReferralOutcome(tenant.id, id, {
                outcome: crOutcome,
                outcomeDetails: crOutcomeDetails,
            });
            setSavedMsg('Care Act referral outcome recorded.');
            setCrOutcomeId(null);
            setCrOutcome('');
            setCrOutcomeDetails('');
            await loadCareReferrals();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to record care referral outcome.');
        } finally {
            setSaving(false);
        }
    };

    const loadProtocolCases = async () => {
        try {
            const res = await rslService.listProtocolCases(tenant.id);
            setPtclList(res.data?.data || []);
            const latest = (res.data?.data || [])[0];
            if (latest) {
                setPtclGround(latest.ground || 'GROUND_8');
                setPtclInviteDate((latest.invitationToRepresent?.sentDate || '').substring(0, 10));
                setPtclInviteMethod(latest.invitationToRepresent?.method || 'post');
                setPtclInviteOfficer(latest.invitationToRepresent?.officer || '');
                setPtclReps((latest.representations || []).map((r: any) => `${r.date?.substring(0, 10)} | ${r.source || ''} | ${r.summary || ''}`).join('\n'));
                setPtclConsiderDate((latest.considerationRecord?.consideredDate || '').substring(0, 10));
                setPtclConsiderOfficer(latest.considerationRecord?.officer || '');
                setPtclConsiderOutcome(latest.considerationRecord?.outcome || 'PROCEEDED');
                setPtclConsiderSummary(latest.considerationRecord?.summary || '');
                setPtclReasons(latest.reasonsForProceeding || '');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load protocol cases');
        } finally {
            setPtclLoaded(true);
        }
    };

    const parseRepresentations = () => ptclReps
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(line => {
            const [date, source, ...rest] = line.split('|').map(s => s.trim());
            return { date: date || undefined, source: source || 'Tenant', summary: rest.join('|').trim() || '' };
        });

    const buildProtocolPatch = () => ({
        ground: ptclGround,
        invitationToRepresent: {
            sentDate: ptclInviteDate ? new Date(ptclInviteDate).toISOString() : undefined,
            method: ptclInviteMethod,
            officer: ptclInviteOfficer || undefined,
        },
        representations: parseRepresentations(),
        considerationRecord: {
            consideredDate: ptclConsiderDate ? new Date(ptclConsiderDate).toISOString() : undefined,
            officer: ptclConsiderOfficer || undefined,
            outcome: ptclConsiderOutcome,
            summary: ptclConsiderSummary || undefined,
        },
        reasonsForProceeding: ptclReasons,
    });

    const handleCreateProtocolCase = async () => {
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            const res = await rslService.createProtocolCase(tenant.id, { ground: ptclGround });
            setSavedMsg('Protocol Part 3 case created (DRAFT).');
            await Promise.all([loadProfile(), loadProtocolCases()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create protocol case.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveProtocolSchedule = async (id: string) => {
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.updateProtocolCase(tenant.id, id, buildProtocolPatch());
            setSavedMsg('Proportionality schedule updated.');
            await Promise.all([loadProfile(), loadProtocolCases()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update schedule.');
        } finally {
            setSaving(false);
        }
    };

    const handleSendForReview = async (id: string) => {
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.updateProtocolCase(tenant.id, id, { status: 'OFFICER_REVIEW' });
            setSavedMsg('Schedule sent for officer review.');
            await Promise.all([loadProfile(), loadProtocolCases()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to send for review.');
        } finally {
            setSaving(false);
        }
    };

    const handleSignOffProtocolCase = async (id: string) => {
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.signOffProtocolCase(tenant.id, id);
            setSavedMsg('Proportionality schedule signed off.');
            await Promise.all([loadProfile(), loadProtocolCases()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to sign off. Reasons for proceeding must be recorded.');
        } finally {
            setSaving(false);
        }
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadSchedule = async (id: string) => {
        try {
            const res = await rslService.downloadSchedulePdf(tenant.id, id);
            downloadBlob(res.data as Blob, `protocol-schedule-${id}.pdf`);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to download schedule PDF.');
        }
    };

    const loadBundles = async () => {
        try {
            const res = await rslService.listEvidenceBundles(tenant.id);
            setBundleList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load evidence bundles');
        } finally {
            setBundleLoaded(true);
        }
    };

    const handleGenerateBundle = async () => {
        setBundleGenerating(true);
        setError(null);
        setSavedMsg(null);
        try {
            const res = await rslService.generateEvidenceBundle(tenant.id);
            setSavedMsg('Evidence bundle compiled and SHA-256 sealed to the Evidence Safe.');
            await Promise.all([loadProfile(), loadBundles()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to compile evidence bundle.');
        } finally {
            setBundleGenerating(false);
        }
    };

    const handleDownloadBundle = async (id: string) => {
        try {
            const res = await rslService.downloadEvidenceBundlePdf(tenant.id, id);
            downloadBlob(res.data as Blob, `evidence-bundle-${id}.pdf`);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to download bundle PDF.');
        }
    };

    // ─── US-057 / F-048: Tenancy sustainment handlers ───
    const loadServices = async () => {
        try {
            const res = await rslService.listSupportServices();
            setSvcList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load support services');
        } finally {
            setSvcLoaded(true);
        }
    };

    const handleCreateService = async () => {
        if (!svcName.trim()) {
            setError('Please enter a service name.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.createSupportService({
                name: svcName.trim(),
                type: svcType,
                contact: svcContact ? { name: svcContact } : undefined,
                referralCriteria: svcCriteria,
                slaDays: Number(svcSla) || 10,
            });
            setSavedMsg('Support service added to directory.');
            setSvcName('');
            setSvcContact('');
            setSvcCriteria('');
            setSvcSla(10);
            await loadServices();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to add support service.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivateService = async (id: string) => {
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.deleteSupportService(id);
            setSavedMsg('Support service deactivated.');
            await loadServices();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to deactivate service.');
        } finally {
            setSaving(false);
        }
    };

    const loadSustainmentReferrals = async () => {
        try {
            const res = await rslService.listSustainmentReferrals(tenant.id);
            setRefList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load sustainment referrals');
        } finally {
            setRefLoaded(true);
        }
    };

    const handleCreateReferral = async () => {
        if (!refServiceId) {
            setError('Please select a support service.');
            return;
        }
        if (!refReason.trim()) {
            setError('Please state the reason for referral.');
            return;
        }
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.createSustainmentReferral(tenant.id, {
                serviceId: refServiceId,
                reasonForReferral: refReason.trim(),
            });
            setSavedMsg('Sustainment referral sent (SLA tracked).');
            setRefServiceId('');
            setRefReason('');
            await loadSustainmentReferrals();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create sustainment referral.');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateReferralStatus = async () => {
        if (!refStatusTarget) return;
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.updateSustainmentReferralStatus(tenant.id, refStatusTarget.id, { status: refStatusTarget.to });
            setSavedMsg(`Referral status updated to ${refStatusTarget.to}.`);
            setRefStatusTarget(null);
            await loadSustainmentReferrals();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update referral status.');
        } finally {
            setSaving(false);
        }
    };

    const handleRecordReferralOutcome = async () => {
        if (!refOutcomeTarget) return;
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.recordSustainmentOutcome(tenant.id, refOutcomeTarget.id, {
                outcome: refOutcomeTarget.outcome,
                outcomeDetails: refOutcomeDetails || refOutcomeTarget.details,
            });
            setSavedMsg(`Outcome recorded: ${refOutcomeTarget.outcome}.`);
            setRefOutcomeTarget(null);
            setRefOutcomeDetails('');
            await loadSustainmentReferrals();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to record outcome.');
        } finally {
            setSaving(false);
        }
    };

    const loadJointAgreements = async () => {
        try {
            const res = await rslService.listJointAgreements(tenant.id);
            setAgrList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load joint protocol agreements');
        } finally {
            setAgrLoaded(true);
        }
    };

    const handleGenerateAgreement = async () => {
        setAgrGenerating(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.generateJointAgreement(tenant.id, { templateType: agrTemplate });
            setSavedMsg('Joint protocol agreement generated and sealed to the Evidence Safe.');
            await loadJointAgreements();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to generate joint protocol agreement.');
        } finally {
            setAgrGenerating(false);
        }
    };

    const handleDownloadAgreement = async (id: string) => {
        try {
            const res = await rslService.downloadJointAgreementPdf(tenant.id, id);
            downloadBlob(res.data as Blob, `joint-protocol-${id}.pdf`);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to download agreement PDF.');
        }
    };

    const loadSustainmentAlerts = async () => {
        try {
            const res = await rslService.listSustainmentAlerts(tenant.id);
            setAltList(res.data?.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load sustainment alerts');
        } finally {
            setAltLoaded(true);
        }
    };

    const handleScanAlerts = async () => {
        setAltScanning(true);
        setError(null);
        setSavedMsg(null);
        try {
            const res = await rslService.scanSustainmentAlerts(tenant.id);
            const created = res.data?.data?.created?.length || 0;
            setSavedMsg(`Sustainment scan complete: ${created} new alert(s) opened.`);
            await Promise.all([loadProfile(), loadSustainmentAlerts()]);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to run sustainment scan.');
        } finally {
            setAltScanning(false);
        }
    };

    const handleActionAlert = async () => {
        if (!altActionTarget) return;
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        try {
            await rslService.updateSustainmentAlert(tenant.id, altActionTarget.id, {
                status: altActionTarget.status,
                notes: altActionNotes,
            });
            setSavedMsg(`Alert ${altActionTarget.status.toLowerCase()}.`);
            setAltActionTarget(null);
            setAltActionNotes('');
            await loadSustainmentAlerts();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update alert.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen || !tenant) return null;
    const trigger = profile?.triggerStatus;
    const triggerBadge = trigger === 'DUE' ? 'bg-red-100 text-red-800 border-red-200'
        : trigger === 'SATISFIED' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
        : 'bg-gray-100 text-gray-600 border-gray-200';

    const fmtDate = (iso?: string) => iso
        ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

    const outcomeLabel = (outcome: string, list: { id: string; label: string }[]) =>
        list.find(o => o.id === outcome)?.label || outcome;

    const MCA_ICONS = { mcaUnderstand: 'fa-brain', mcaRetain: 'fa-database', mcaWeigh: 'fa-scale-balanced', mcaCommunicate: 'fa-comments' };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="rsl-modal-title">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 sm:mx-0 sm:h-10 sm:w-10">
                                <i className="fas fa-shield-halved text-rose-600"></i>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="rsl-modal-title">
                                    Safeguarding & Pre-Action Protocol — {tenant.name}
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        RSL Pre-Action Protocol compliance: vulnerability assessment (US-051) and MCA 2005 capacity screening (US-052). All records are SHA-256 logged to the Evidence Safe.
                                    </p>

                                    {loading && (
                                        <div className="mt-4 text-center py-6">
                                            <div className="animate-spin h-8 w-8 border-4 border-rose-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                                            <p className="text-sm text-gray-500">Loading safeguarding profile…</p>
                                        </div>
                                    )}

                                    {error && !loading && (
                                        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                                            <i className="fas fa-exclamation-circle mr-2"></i>{error}
                                        </div>
                                    )}

                                    {savedMsg && (
                                        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-700">
                                            <i className="fas fa-check-circle mr-2"></i>{savedMsg}
                                        </div>
                                    )}

                                    {!loading && (
                                        <>
                                            {/* Trigger status */}
                                            {trigger && (
                                                <div className={`mt-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${triggerBadge}`}>
                                                    <i className={`fas ${trigger === 'DUE' ? 'fa-exclamation-triangle' : trigger === 'SATISFIED' ? 'fa-check' : 'fa-circle' } mr-1.5`}></i>
                                                    {trigger === 'DUE' ? 'Assessment due' : trigger === 'SATISFIED' ? 'Trigger satisfied' : 'No active trigger'}
                                                </div>
                                            )}
                                            {profile?.triggerReason && (
                                                <p className="mt-2 text-xs text-gray-500 italic">{profile.triggerReason}</p>
                                            )}

                                            {/* Tabs */}
                                            <div className="mt-4 border-b border-gray-200">
                                                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                                                    {[
                                                        { id: 'profile', label: 'Profile & Evidence Safe' },
                                                        { id: 'vuln', label: 'Vulnerability Assessment' },
                                                        { id: 'capacity', label: 'Capacity Screen' },
                                                        { id: 'adj', label: 'Reasonable Adjustments' },
                                                        { id: 'care', label: 'Care Act Referral' },
                                                        { id: 'protocol', label: 'Protocol Part 3' },
                                                        { id: 'bundle', label: 'Evidence Bundle' },
                                                        { id: 'sustain', label: 'Tenancy Sustainment' },
                                                    ].map(t => (
                                                        <button
                                                            key={t.id}
                                                            onClick={() => { setTab(t.id as any); setError(null); setSavedMsg(null); }}
                                                            className={`py-2 px-1 text-sm font-medium border-b-2 ${
                                                                tab === t.id
                                                                    ? 'border-rose-500 text-rose-600'
                                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                            }`}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </nav>
                                            </div>

                                            {/* PROFILE TAB */}
                                            {tab === 'profile' && (
                                                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="md:col-span-3">
                                                        {compliance && (
                                                            <div className={`rounded-lg border p-4 ${
                                                                compliance.rag === 'GREEN' ? 'bg-emerald-50 border-emerald-200'
                                                                : compliance.rag === 'AMBER' ? 'bg-amber-50 border-amber-200'
                                                                : 'bg-rose-50 border-rose-200'}`}>
                                                                <div className="flex items-center justify-between flex-wrap gap-3">
                                                                    <div className="flex items-center">
                                                                        <span className={`h-3 w-3 rounded-full mr-2 ${
                                                                            compliance.rag === 'GREEN' ? 'bg-emerald-500'
                                                                            : compliance.rag === 'AMBER' ? 'bg-amber-500'
                                                                            : 'bg-rose-500'}`}></span>
                                                                        <span className="text-sm font-semibold text-gray-900">Protocol Compliance — {compliance.rag}</span>
                                                                        <span className="ml-2 text-sm text-gray-600">{compliance.completionPct}% ({compliance.doneCount}/{compliance.totalCount})</span>
                                                                    </div>
                                                                    {compliance.daysSinceLastAction !== null && (
                                                                        <span className="text-xs text-gray-500">Days since last action: {compliance.daysSinceLastAction}</span>
                                                                    )}
                                                                    {compliance.latestBundle && (
                                                                        <span className="text-xs text-gray-500">Bundle ready: {compliance.latestBundle.totalPages} page(s) · SHA-256 {compliance.latestBundle.pdfHash?.substring(0, 12)}…</span>
                                                                    )}
                                                                </div>
                                                                {compliance.missingItems?.length > 0 && (
                                                                    <div className="mt-2">
                                                                        <p className="text-xs font-medium text-gray-700 mb-1">Missing for full compliance:</p>
                                                                        <ul className="list-disc list-inside text-xs text-gray-600">
                                                                            {compliance.missingItems.map((m: string) => <li key={m}>{m}</li>)}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="md:col-span-1 space-y-4">
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Latest Vulnerability Assessment</h4>
                                                            {profile?.latestVulnerability ? (
                                                                <div className="space-y-1 text-sm">
                                                                    <div><span className="text-gray-500">Stage:</span> {outcomeLabel(profile.latestVulnerability.stage, VULN_STAGES)}</div>
                                                                    <div>
                                                                        <span className="text-gray-500">Outcome:</span>{' '}
                                                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${OUTCOME_STYLES[profile.latestVulnerability.outcome === 'NO_VULNERABILITY' ? 'green' : profile.latestVulnerability.outcome === 'VULNERABILITY_IDENTIFIED' ? 'amber' : 'red']}`}>
                                                                            {outcomeLabel(profile.latestVulnerability.outcome, VULN_OUTCOMES)}
                                                                        </span>
                                                                    </div>
                                                                    <div><span className="text-gray-500">Assessed:</span> {fmtDate(profile.latestVulnerability.assessedAt)}</div>
                                                                    <div className="text-xs text-gray-400 font-mono break-all">SHA-256: {profile.latestVulnerability.recordHash?.substring(0, 24)}…</div>
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-gray-500">No vulnerability assessment recorded yet.</p>
                                                            )}
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Latest Capacity Screen</h4>
                                                            {profile?.latestCapacity ? (
                                                                <div className="space-y-1 text-sm">
                                                                    <div>
                                                                        <span className="text-gray-500">Outcome:</span>{' '}
                                                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${OUTCOME_STYLES[profile.latestCapacity.outcome === 'CAPACITY_PRESUMED' ? 'green' : profile.latestCapacity.outcome === 'CAPACITY_DOUBTFUL' ? 'amber' : 'red']}`}>
                                                                            {outcomeLabel(profile.latestCapacity.outcome, CAPACITY_OUTCOMES)}
                                                                        </span>
                                                                    </div>
                                                                    <div><span className="text-gray-500">Assessed:</span> {fmtDate(profile.latestCapacity.assessedAt)}</div>
                                                                    {profile.latestCapacity.litigationFriend?.pathwayOpen && (
                                                                        <div className="text-xs text-rose-700 font-semibold">CPR 21 litigation-friend pathway open</div>
                                                                    )}
                                                                    <div className="text-xs text-gray-400 font-mono break-all">SHA-256: {profile.latestCapacity.recordHash?.substring(0, 24)}…</div>
                                                                </div>
                                                            ) : (
                                                                <p className="text-sm text-gray-500">No capacity screen recorded yet.</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="md:col-span-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Evidence Safe Log</h4>
                                                        {(profile?.safeLog?.length || 0) === 0 ? (
                                                            <p className="text-sm text-gray-500">No Evidence Safe entries yet.</p>
                                                        ) : (
                                                            <ul className="space-y-2 max-h-80 overflow-y-auto">
                                                                {profile.safeLog.map((entry: any) => (
                                                                    <li key={entry.id} className="bg-white border border-gray-200 rounded p-3 text-sm">
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="font-medium text-gray-800">{entry.action?.replace(/_/g, ' ')}</span>
                                                                            <span className="text-xs text-gray-400">{fmtDate(entry.loggedAt)}</span>
                                                                        </div>
                                                                        {entry.details && <p className="text-xs text-gray-500 mt-1">{entry.details}</p>}
                                                                        <div className="text-[10px] text-gray-400 font-mono mt-1 break-all">SHA-256: {entry.recordHash}</div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* VULNERABILITY TAB */}
                                            {tab === 'vuln' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Stage</label>
                                                        <div className="space-y-2">
                                                            {VULN_STAGES.map(s => (
                                                                <label key={s.id} className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                                                    <input type="radio" name="vstage" checked={vStage === s.id} onChange={() => setVStage(s.id)} className="mt-1 mr-2" />
                                                                    <span>
                                                                        <span className="text-sm font-medium text-gray-800">{s.label}</span>
                                                                        <span className="block text-xs text-gray-500">{s.description}</span>
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Outcome</label>
                                                        <div className="space-y-2">
                                                            {VULN_OUTCOMES.map(o => (
                                                                <label key={o.id} className="flex items-center p-2 border rounded cursor-pointer hover:bg-gray-50">
                                                                    <input type="radio" name="voutcome" checked={vOutcome === o.id} onChange={() => setVOutcome(o.id)} className="mr-2" />
                                                                    <span className="text-sm text-gray-800">{o.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {vOutcome === 'VULNERABILITY_IDENTIFIED' && (
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vulnerability Types</label>
                                                            <div className="flex flex-wrap gap-2">
                                                                {VULN_TYPES.map(t => (
                                                                    <button key={t} type="button" onClick={() => toggleIn(vTypes, setVTypes, t)}
                                                                        className={`px-2 py-1 rounded text-xs border ${vTypes.includes(t) ? 'bg-rose-100 border-rose-300 text-rose-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                                        {t}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Protected Characteristics (Equality Act 2010)</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {PROTECTED_CHARACTERISTICS.map(t => (
                                                                <button key={t} type="button" onClick={() => toggleIn(vProtected, setVProtected, t)}
                                                                    className={`px-2 py-1 rounded text-xs border ${vProtected.includes(t) ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                                    {t}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Care Needs (Care Act 2014)</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {CARE_NEEDS.map(t => (
                                                                <button key={t} type="button" onClick={() => toggleIn(vCare, setVCare, t)}
                                                                    className={`px-2 py-1 rounded text-xs border ${vCare.includes(t) ? 'bg-teal-100 border-teal-300 text-teal-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                                    {t}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Communication Needs</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {COMMUNICATION_NEEDS.map(t => (
                                                                <button key={t} type="button" onClick={() => toggleIn(vComms, setVComms, t)}
                                                                    className={`px-2 py-1 rounded text-xs border ${vComms.includes(t) ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                                    {t}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Support Agency Involvement</label>
                                                        <div className="flex flex-wrap gap-2">
                                                            {SUPPORT_AGENCIES.map(t => (
                                                                <button key={t} type="button" onClick={() => toggleIn(vAgencies, setVAgencies, t)}
                                                                    className={`px-2 py-1 rounded text-xs border ${vAgencies.includes(t) ? 'bg-purple-100 border-purple-300 text-purple-800' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                                                                    {t}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                                        <textarea value={vNotes} onChange={e => setVNotes(e.target.value)} rows={3}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Officer notes on contact, observations, or support already offered…" />
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button onClick={handleSaveVulnerability} disabled={saving}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                            {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-shield-halved mr-2"></i>}
                                                            Record Assessment (Evidence Safe)
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* CAPACITY TAB */}
                                            {tab === 'capacity' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                                                        <i className="fas fa-info-circle mr-2"></i>
                                                        Mental Capacity Act 2005 — four-stage functional test. Capacity is assumed unless the four-stage test shows otherwise (MCA s.1(2)).
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">Outcome</label>
                                                        <div className="space-y-2">
                                                            {CAPACITY_OUTCOMES.map(o => (
                                                                <label key={o.id} className="flex items-center p-2 border rounded cursor-pointer hover:bg-gray-50">
                                                                    <input type="radio" name="coutcome" checked={cOutcome === o.id} onChange={() => setCOutcome(o.id)} className="mr-2" />
                                                                    <span className="text-sm text-gray-800">{o.label}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">MCA 2005 Four-Stage Test</label>
                                                        <div className="space-y-3">
                                                            {MCA_STAGES.map(stage => (
                                                                <div key={stage.key} className="border border-gray-200 rounded p-3 bg-gray-50">
                                                                    <div className="flex items-center justify-between">
                                                                        <div>
                                                                            <span className="text-sm font-medium text-gray-800">
                                                                                <i className={`fas ${MCA_ICONS[stage.key as keyof typeof MCA_ICONS]} text-rose-500 mr-1.5`}></i>
                                                                                {stage.label}
                                                                            </span>
                                                                            <span className="block text-xs text-gray-500">{stage.help}</span>
                                                                        </div>
                                                                        <div className="flex gap-2">
                                                                            {[{ v: true, l: 'Can' }, { v: false, l: 'Cannot' }].map(opt => (
                                                                                <button key={String(opt.v)} type="button"
                                                                                    onClick={() => setCStages(prev => ({ ...prev, [stage.key]: { ...prev[stage.key], able: opt.v } }))}
                                                                                    className={`px-3 py-1 rounded text-xs border ${cStages[stage.key].able === opt.v ? (opt.v ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-rose-100 border-rose-300 text-rose-800') : 'bg-white border-gray-300 text-gray-600'}`}>
                                                                                    {opt.l}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                    <input
                                                                        value={cStages[stage.key].notes}
                                                                        onChange={e => setCStages(prev => ({ ...prev, [stage.key]: { ...prev[stage.key], notes: e.target.value } }))}
                                                                        placeholder="Notes / evidence for this stage…"
                                                                        className="mt-2 w-full border border-gray-300 rounded-md p-2 text-sm"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Officer Notes</label>
                                                        <textarea value={cNotes} onChange={e => setCNotes(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Summary of the screening and any recommended next steps…" />
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button onClick={handleSaveCapacity} disabled={saving}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                            {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-brain mr-2"></i>}
                                                            Record Capacity Screen (Evidence Safe)
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* REASONABLE ADJUSTMENTS TAB (US-053) */}
                                            {tab === 'adj' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                                                        <i className="fas fa-info-circle mr-2"></i>
                                                        Equality Act 2010 — reasonable adjustments record. A record of this adjustment is written to the Evidence Safe (SHA-256) for the pre-action bundle.
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
                                                            <select value={aType} onChange={e => setAType(e.target.value)}
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                                                <option value="communication">Communication</option>
                                                                <option value="visit">Visit / in-person</option>
                                                                <option value="timescale">Timescale / response time</option>
                                                                <option value="advocate">Advocate / representative</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Authorised By</label>
                                                            <input value={aAuthorisedBy} onChange={e => setAAuthorisedBy(e.target.value)}
                                                                placeholder="e.g. Housing officer name"
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Applies From</label>
                                                            <input type="date" value={aAppliedFrom} onChange={e => setAAppliedFrom(e.target.value)}
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Review Date</label>
                                                            <input type="date" value={aReviewDate} onChange={e => setAReviewDate(e.target.value)}
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Detail</label>
                                                        <textarea value={aDetail} onChange={e => setADetail(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="What adjustment is being made? e.g. All arrears correspondence by email and large-print letter; 10 working days to respond to notices…" />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                                        <textarea value={aNotes} onChange={e => setANotes(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Context, tenant request, or reason for the adjustment…" />
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button onClick={handleSaveAdjustment} disabled={saving}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                            {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-person-cane mr-2"></i>}
                                                            Record Adjustment (Evidence Safe)
                                                        </button>
                                                    </div>

                                                    {!adjLoaded && (
                                                        <button onClick={loadAdjustments} className="text-sm text-rose-600 underline">
                                                            Load existing adjustments
                                                        </button>
                                                    )}

                                                    {adjList.length > 0 && (
                                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Detail</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Applies</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Review</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SHA-256</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-200">
                                                                    {adjList.map(a => (
                                                                        <tr key={a.id}>
                                                                            <td className="px-3 py-2 capitalize text-gray-800">{a.adjustmentType}</td>
                                                                            <td className="px-3 py-2 text-gray-800">{a.detail}</td>
                                                                            <td className="px-3 py-2 text-gray-500">{fmtDate(a.appliedFrom)}</td>
                                                                            <td className="px-3 py-2 text-gray-500">{fmtDate(a.reviewDate)}</td>
                                                                            <td className="px-3 py-2 font-mono text-xs text-gray-400">{a.recordHash?.substring(0, 12)}…</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* CARE ACT TAB (US-054) */}
                                            {tab === 'care' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                                                        <i className="fas fa-info-circle mr-2"></i>
                                                        Care Act 2014 — referral to Adult Social Care for a community care assessment. Referrals are tracked against a 28-day SLA and the outcome feeds your proportionality evidence.
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Type</label>
                                                            <select value={crType} onChange={e => setCrType(e.target.value)}
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                                                <option value="community_care">Community care assessment</option>
                                                                <option value="safeguarding">Safeguarding (s.42)</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Referring Officer</label>
                                                            <input value={crOfficer} onChange={e => setCrOfficer(e.target.value)}
                                                                placeholder="Name of referring officer"
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Referral</label>
                                                        <textarea value={crReason} onChange={e => setCrReason(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Why is an assessment needed? e.g. Tenant unable to manage finances or engage with the tenancy…" />
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Care Needs Summary</label>
                                                        <textarea value={crNeeds} onChange={e => setCrNeeds(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Observed care and support needs…" />
                                                    </div>

                                                    <div>
                                                        <label className="flex items-center p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                                                            <input type="checkbox" checked={crConsent} onChange={e => setCrConsent(e.target.checked)} className="mr-2" />
                                                            <span className="text-sm text-gray-800">Consent given to share tenant information with Adult Social Care</span>
                                                        </label>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                                        <textarea value={crNotes} onChange={e => setCrNotes(e.target.value)} rows={2}
                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                                                            placeholder="Any additional context for the local authority…" />
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button onClick={handleSaveCareReferral} disabled={saving}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                            {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-hand-holding-heart mr-2"></i>}
                                                            Submit Care Act Referral (Evidence Safe)
                                                        </button>
                                                    </div>

                                                    {!careLoaded && (
                                                        <button onClick={loadCareReferrals} className="text-sm text-rose-600 underline">
                                                            Load existing referrals
                                                        </button>
                                                    )}

                                                    {careList.length > 0 && (
                                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Submitted</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SLA Due</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Outcome</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-200">
                                                                    {careList.map(c => (
                                                                        <tr key={c.id}>
                                                                            <td className="px-3 py-2 text-gray-800">{c.status}</td>
                                                                            <td className="px-3 py-2 text-gray-800 capitalize">{c.requestedAssessmentType.replace('_', ' ')}</td>
                                                                            <td className="px-3 py-2 text-gray-500">{fmtDate(c.submittedAt)}</td>
                                                                            <td className="px-3 py-2 text-gray-500">{fmtDate(c.slaDueAt)}</td>
                                                                            <td className="px-3 py-2 capitalize text-gray-800">{c.outcome || '-'}</td>
                                                                            <td className="px-3 py-2">
                                                                                {!['ASSESSMENT_COMPLETED', 'DECLINED', 'NOT_ELIGIBLE'].includes(c.status) ? (
                                                                                    <button
                                                                                        onClick={() => { setCrOutcomeId(c.id); setCrOutcome(''); setCrOutcomeDetails(''); }}
                                                                                        className="text-xs text-rose-600 underline">Record outcome</button>
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400">Closed</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}

                                                    {crOutcomeId && (
                                                        <div className="border border-rose-200 rounded-lg p-4 bg-rose-50 space-y-3">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Outcome</label>
                                                                <select value={crOutcome} onChange={e => setCrOutcome(e.target.value)}
                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                                                    <option value="">Select outcome…</option>
                                                                    <option value="completed">Assessment completed</option>
                                                                    <option value="declined">Declined</option>
                                                                    <option value="not_eligible">Not eligible for care act assessment</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Outcome Details</label>
                                                                <textarea value={crOutcomeDetails} onChange={e => setCrOutcomeDetails(e.target.value)} rows={2}
                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm"
                                                                    placeholder="Summary of the LA decision or outcome…" />
                                                            </div>
                                                            <div className="flex justify-end gap-2">
                                                                <button onClick={() => { setCrOutcomeId(null); setCrOutcome(''); setCrOutcomeDetails(''); }}
                                                                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-600">Cancel</button>
                                                                <button onClick={() => handleCareOutcome(crOutcomeId)} disabled={saving}
                                                                    className="px-3 py-1.5 text-sm rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : null} Save Outcome
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* PROTOCOL PART 3 TAB (US-055 / T-047-005) */}
                                            {tab === 'protocol' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">
                                                        <i className="fas fa-scale-balanced mr-2"></i>
                                                        Protocol Part 3 proportionality schedule (Pre-Action Protocol para 3.3): invitation to represent, representations received, consideration record, reasons for proceeding, and supporting documents. Officer review and sign-off required before filing.
                                                    </div>

                                                    {!ptclLoaded && (
                                                        <button onClick={loadProtocolCases} className="text-sm text-rose-600 underline">
                                                            Load existing protocol cases
                                                        </button>
                                                    )}

                                                    {ptclList.length === 0 ? (
                                                        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-700 mb-1">Mandatory Ground</label>
                                                                <select value={ptclGround} onChange={e => setPtclGround(e.target.value)}
                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                                                    <option value="GROUND_8">Ground 8 — serious rent arrears (8 weeks)</option>
                                                                    <option value="GROUND_10">Ground 10 — some rent arrears</option>
                                                                    <option value="GROUND_11">Ground 11 — persistent delay in paying rent</option>
                                                                </select>
                                                            </div>
                                                            <div className="flex justify-end">
                                                                <button onClick={handleCreateProtocolCase} disabled={saving}
                                                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-file-lines mr-2"></i>}
                                                                    Create Protocol Part 3 Case
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        (() => {
                                                            const latest = ptclList[0];
                                                            const canEdit = latest.status !== 'SIGNED_OFF';
                                                            return (
                                                                <div className="space-y-4">
                                                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${
                                                                            latest.status === 'SIGNED_OFF' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                                                            : latest.status === 'OFFICER_REVIEW' ? 'bg-amber-100 text-amber-800 border-amber-200'
                                                                            : 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                                                                            {latest.status.replace(/_/g, ' ')}
                                                                        </span>
                                                                        <div className="text-xs text-gray-400 font-mono break-all">SHA-256: {latest.recordHash}</div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">Mandatory Ground</label>
                                                                        <select value={ptclGround} onChange={e => setPtclGround(e.target.value)} disabled={!canEdit}
                                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100">
                                                                            <option value="GROUND_8">Ground 8 — serious rent arrears (8 weeks)</option>
                                                                            <option value="GROUND_10">Ground 10 — some rent arrears</option>
                                                                            <option value="GROUND_11">Ground 11 — persistent delay in paying rent</option>
                                                                        </select>
                                                                    </div>

                                                                    <div className="border border-gray-200 rounded-lg p-3">
                                                                        <p className="text-sm font-semibold text-gray-900 mb-2">(a) Invitation to the tenant to make representations</p>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Date sent</label>
                                                                                <input type="date" value={ptclInviteDate} onChange={e => setPtclInviteDate(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100" />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Method</label>
                                                                                <select value={ptclInviteMethod} onChange={e => setPtclInviteMethod(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100">
                                                                                    <option value="post">Post</option>
                                                                                    <option value="email">Email</option>
                                                                                    <option value="hand_delivery">Hand delivery</option>
                                                                                </select>
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Officer</label>
                                                                                <input value={ptclInviteOfficer} onChange={e => setPtclInviteOfficer(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100" />
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="border border-gray-200 rounded-lg p-3">
                                                                        <p className="text-sm font-semibold text-gray-900 mb-2">(b) Representations received — one per line: <span className="font-normal text-gray-500">date | source | summary</span></p>
                                                                        <textarea value={ptclReps} onChange={e => setPtclReps(e.target.value)} rows={3} disabled={!canEdit}
                                                                            placeholder={"2026-08-05 | Tenant phone call | Benefits under reassessment"}
                                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100" />
                                                                    </div>

                                                                    <div className="border border-gray-200 rounded-lg p-3">
                                                                        <p className="text-sm font-semibold text-gray-900 mb-2">(c) Consideration of representations</p>
                                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Date considered</label>
                                                                                <input type="date" value={ptclConsiderDate} onChange={e => setPtclConsiderDate(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100" />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Officer</label>
                                                                                <input value={ptclConsiderOfficer} onChange={e => setPtclConsiderOfficer(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100" />
                                                                            </div>
                                                                            <div>
                                                                                <label className="block text-xs text-gray-500 mb-1">Outcome</label>
                                                                                <select value={ptclConsiderOutcome} onChange={e => setPtclConsiderOutcome(e.target.value)} disabled={!canEdit}
                                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm disabled:bg-gray-100">
                                                                                    <option value="PROCEEDED">Proceed with possession</option>
                                                                                    <option value="NOT_PROCEEDING">Not proceeding</option>
                                                                                    <option value="FURTHER_ENGAGEMENT">Further engagement</option>
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-xs text-gray-500 mb-1">Summary of consideration</label>
                                                                            <textarea value={ptclConsiderSummary} onChange={e => setPtclConsiderSummary(e.target.value)} rows={2} disabled={!canEdit}
                                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100" />
                                                                        </div>
                                                                    </div>

                                                                    <div className="border border-gray-200 rounded-lg p-3">
                                                                        <p className="text-sm font-semibold text-gray-900 mb-2">(d) Reasons for proceeding</p>
                                                                        <textarea value={ptclReasons} onChange={e => setPtclReasons(e.target.value)} rows={3} disabled={!canEdit}
                                                                            placeholder="Why possession remains proportionate despite the above…"
                                                                            className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100" />
                                                                    </div>

                                                                    <div className="flex flex-wrap justify-end gap-2">
                                                                        {canEdit && (
                                                                            <>
                                                                                <button onClick={() => handleSaveProtocolSchedule(latest.id)} disabled={saving}
                                                                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-save mr-2"></i>}
                                                                                    Save Schedule
                                                                                </button>
                                                                                {latest.status === 'DRAFT' && (
                                                                                    <button onClick={() => handleSendForReview(latest.id)} disabled={saving}
                                                                                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                                                                        Send for Review
                                                                                    </button>
                                                                                )}
                                                                                <button onClick={() => handleSignOffProtocolCase(latest.id)} disabled={saving}
                                                                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-signature mr-2"></i>}
                                                                                    Sign Off
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        <button onClick={() => handleDownloadSchedule(latest.id)}
                                                                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50">
                                                                            <i className="fas fa-file-pdf mr-2"></i> Download Schedule PDF
                                                                        </button>
                                                                    </div>

                                                                    {ptclList.length > 1 && (
                                                                        <div className="text-xs text-gray-500">
                                                                            {ptclList.length - 1} earlier case(s) on record.
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()
                                                    )}
                                                </div>
                                            )}

                                            {/* EVIDENCE BUNDLE TAB (US-055 / T-047-006) */}
                                            {tab === 'bundle' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">
                                                        <i className="fas fa-book mr-2"></i>
                                                        Compile all pre-action documents into an indexed, paginated, court-ready PDF: cover page, Protocol Part 3 schedule, vulnerability assessments, capacity screens, reasonable adjustments, Care Act referrals and the Evidence Safe log. Each bundle is SHA-256 sealed on generation.
                                                    </div>

                                                    <div className="flex justify-end">
                                                        <button onClick={handleGenerateBundle} disabled={bundleGenerating}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                            {bundleGenerating ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-file-circle-plus mr-2"></i>}
                                                            Generate Evidence Bundle
                                                        </button>
                                                    </div>

                                                    {!bundleLoaded && (
                                                        <button onClick={loadBundles} className="text-sm text-rose-600 underline">
                                                            Load existing bundles
                                                        </button>
                                                    )}

                                                    {bundleList.length > 0 && (
                                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                <thead className="bg-gray-50">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Generated</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Pages</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Documents</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SHA-256</th>
                                                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-200">
                                                                    {bundleList.map(b => (
                                                                        <tr key={b.id}>
                                                                            <td className="px-3 py-2 text-gray-800">{fmtDate(b.generatedAt)}</td>
                                                                            <td className="px-3 py-2 text-gray-800">{b.totalPages}</td>
                                                                            <td className="px-3 py-2 text-gray-800">{b.docCount}</td>
                                                                            <td className="px-3 py-2 font-mono text-xs text-gray-400">{b.pdfHash?.substring(0, 16)}…</td>
                                                                            <td className="px-3 py-2 space-x-3">
                                                                                <button onClick={() => handleDownloadBundle(b.id)} className="text-xs text-rose-600 underline">
                                                                                    <i className="fas fa-file-pdf mr-1"></i>Download PDF
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* TENANCY SUSTAINMENT TAB (US-057 / F-048) */}
                                            {tab === 'sustain' && (
                                                <div className="mt-4 space-y-4 text-left">
                                                    <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-800">
                                                        <i className="fas fa-handshake-angle mr-2"></i>
                                                        Tenancy sustainment (Protocol para 1.2): refer the tenant to floating support, mental health, substance misuse, debt advice or adult social care services and track outcomes to demonstrate proactive sustainment before any pre-action stage.
                                                    </div>

                                                    {/* Service directory (T-048-001) */}
                                                    <div className="border border-gray-200 rounded-lg p-4">
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-3">1. Service Directory</h4>
                                                        {!svcLoaded && (
                                                            <button onClick={loadServices} className="text-sm text-rose-600 underline mb-2">Load service directory</button>
                                                        )}
                                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                                                            <input value={svcName} onChange={e => setSvcName(e.target.value)} placeholder="Service name"
                                                                className="md:col-span-2 border border-gray-300 rounded-md p-2 text-sm" />
                                                            <select value={svcType} onChange={e => setSvcType(e.target.value)}
                                                                className="border border-gray-300 rounded-md p-2 text-sm">
                                                                <option value="floating_support">Floating support</option>
                                                                <option value="cmht">Mental health (CMHT)</option>
                                                                <option value="drug_alcohol">Drug / alcohol</option>
                                                                <option value="debt_advice">Debt advice</option>
                                                                <option value="asc">Adult social care</option>
                                                            </select>
                                                            <input value={svcContact} onChange={e => setSvcContact(e.target.value)} placeholder="Contact"
                                                                className="border border-gray-300 rounded-md p-2 text-sm" />
                                                            <input value={svcSla} onChange={e => setSvcSla(Number(e.target.value))} type="number" min={1} placeholder="SLA (days)"
                                                                className="border border-gray-300 rounded-md p-2 text-sm" />
                                                            <button onClick={handleCreateService} disabled={saving}
                                                                className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-plus mr-2"></i>}Add
                                                            </button>
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-gray-500 mb-1">Referral criteria</label>
                                                            <textarea value={svcCriteria} onChange={e => setSvcCriteria(e.target.value)} rows={1}
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                        </div>
                                                        {svcList.length > 0 && (
                                                            <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Service</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Type</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SLA</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500"></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-200">
                                                                        {svcList.map(s => (
                                                                            <tr key={s.id}>
                                                                                <td className="px-3 py-2 text-gray-800">{s.name}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{s.type.replace(/_/g, ' ')}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{s.slaDays}d</td>
                                                                                <td className="px-3 py-2 text-gray-600">{s.active ? 'Active' : 'Inactive'}</td>
                                                                                <td className="px-3 py-2">
                                                                                    {s.active && (
                                                                                        <button onClick={() => handleDeactivateService(s.id)} className="text-xs text-gray-500 underline">
                                                                                            Deactivate
                                                                                        </button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* One-click referral (T-048-001) */}
                                                    <div className="border border-gray-200 rounded-lg p-4">
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-3">2. Sustainment Referral</h4>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                                            <div className="md:col-span-1">
                                                                <label className="block text-xs text-gray-500 mb-1">Service</label>
                                                                {svcList.length === 0 ? (
                                                                    <button onClick={loadServices} className="text-xs text-rose-600 underline">Load services first</button>
                                                                ) : (
                                                                    <select value={refServiceId} onChange={e => setRefServiceId(e.target.value)}
                                                                        className="w-full border border-gray-300 rounded-md p-2 text-sm">
                                                                        <option value="">Select service…</option>
                                                                        {svcList.filter(s => s.active).map(s => (
                                                                            <option key={s.id} value={s.id}>{s.name}</option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </div>
                                                            <div className="md:col-span-2">
                                                                <label className="block text-xs text-gray-500 mb-1">Reason for referral</label>
                                                                <textarea value={refReason} onChange={e => setRefReason(e.target.value)} rows={2}
                                                                    placeholder="Why is the tenant being referred (e.g. rent arrears + health condition)?"
                                                                    className="w-full border border-gray-300 rounded-md p-2 text-sm" />
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <button onClick={handleCreateReferral} disabled={saving}
                                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-paper-plane mr-2"></i>}
                                                                Send Referral
                                                            </button>
                                                        </div>
                                                        {!refLoaded && (
                                                            <button onClick={loadSustainmentReferrals} className="text-sm text-rose-600 underline mt-2">Load existing referrals</button>
                                                        )}
                                                        {refList.length > 0 && (
                                                            <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Service</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SLA due</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Outcome</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500"></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-200">
                                                                        {refList.map(r => (
                                                                            <tr key={r.id}>
                                                                                <td className="px-3 py-2 text-gray-800">{r.serviceName}</td>
                                                                                <td className="px-3 py-2">
                                                                                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${
                                                                                        r.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                                                                        : r.status === 'DECLINED' ? 'bg-gray-100 text-gray-600 border-gray-200'
                                                                                        : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                                                                                        {r.status.replace(/_/g, ' ')}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="px-3 py-2 text-gray-600">{fmtDate(r.slaDueAt)}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{r.outcome || '—'}</td>
                                                                                <td className="px-3 py-2 space-x-3">
                                                                                    {r.status === 'SENT' && (
                                                                                        <button onClick={() => setRefStatusTarget({ id: r.id, to: 'ACKNOWLEDGED' })} className="text-xs text-rose-600 underline">Acknowledge</button>
                                                                                    )}
                                                                                    {r.status === 'ACKNOWLEDGED' && (
                                                                                        <button onClick={() => setRefStatusTarget({ id: r.id, to: 'ENGAGED' })} className="text-xs text-rose-600 underline">Mark engaged</button>
                                                                                    )}
                                                                                    {r.status === 'ENGAGED' && (
                                                                                        <button onClick={() => setRefOutcomeTarget({ id: r.id, outcome: 'SUSTAINED', details: '' })} className="text-xs text-emerald-600 underline">Record outcome</button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Status / outcome inline editors */}
                                                    {refStatusTarget && (
                                                        <div className="border border-gray-200 rounded-lg p-4">
                                                            <p className="text-sm text-gray-800 mb-3">Move referral to <strong>{refStatusTarget.to.replace(/_/g, ' ')}</strong>?</p>
                                                            <div className="flex justify-end gap-2">
                                                                <button onClick={() => setRefStatusTarget(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-600">Cancel</button>
                                                                <button onClick={handleUpdateReferralStatus} disabled={saving}
                                                                    className="px-3 py-1.5 text-sm rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : null} Update
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {refOutcomeTarget && (
                                                        <div className="border border-gray-200 rounded-lg p-4">
                                                            <p className="text-sm text-gray-800 mb-2">Sustainment outcome</p>
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                                                                {['SUSTAINED', 'ESCALATED', 'EVICTED'].map(o => (
                                                                    <button key={o} onClick={() => setRefOutcomeTarget({ id: refOutcomeTarget.id, outcome: o, details: '' })}
                                                                        className={`px-3 py-2 text-sm rounded-md border ${
                                                                            refOutcomeTarget.outcome === o ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                                                                        {o.replace(/_/g, ' ')}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <textarea value={refOutcomeDetails} onChange={e => setRefOutcomeDetails(e.target.value)} rows={2}
                                                                placeholder="Outcome details…"
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm mb-2" />
                                                            <div className="flex justify-end gap-2">
                                                                <button onClick={() => { setRefOutcomeTarget(null); setRefOutcomeDetails(''); }} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-600">Cancel</button>
                                                                <button onClick={handleRecordReferralOutcome} disabled={saving}
                                                                    className="px-3 py-1.5 text-sm rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : null} Save Outcome
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Joint protocol agreements (T-048-002) */}
                                                    <div className="border border-gray-200 rounded-lg p-4">
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-3">3. Joint Protocol Agreements</h4>
                                                        <p className="text-xs text-gray-500 mb-3">Multi-agency working templates (hospital discharge, prison release, care leaver, domestic abuse). Each generates a signed agreement PDF sealed to the Evidence Safe.</p>
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                                            <select value={agrTemplate} onChange={e => setAgrTemplate(e.target.value)}
                                                                className="border border-gray-300 rounded-md p-2 text-sm">
                                                                <option value="HOSPITAL_DISCHARGE">Hospital discharge</option>
                                                                <option value="PRISON_RELEASE">Prison release</option>
                                                                <option value="CARE_LEAVER">Care leaver</option>
                                                                <option value="DOMESTIC_ABUSE">Domestic abuse</option>
                                                            </select>
                                                            <button onClick={handleGenerateAgreement} disabled={agrGenerating}
                                                                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                {agrGenerating ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-file-signature mr-2"></i>}
                                                                Generate Agreement
                                                            </button>
                                                        </div>
                                                        {!agrLoaded && (
                                                            <button onClick={loadJointAgreements} className="text-sm text-rose-600 underline">Load existing agreements</button>
                                                        )}
                                                        {agrList.length > 0 && (
                                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Template</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">SHA-256</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500"></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-200">
                                                                        {agrList.map(a => (
                                                                            <tr key={a.id}>
                                                                                <td className="px-3 py-2 text-gray-800">{a.templateType.replace(/_/g, ' ')}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{a.status}</td>
                                                                                <td className="px-3 py-2 font-mono text-xs text-gray-400">{a.pdfHash?.substring(0, 16)}…</td>
                                                                                <td className="px-3 py-2">
                                                                                    <button onClick={() => handleDownloadAgreement(a.id)} className="text-xs text-rose-600 underline">
                                                                                        <i className="fas fa-file-pdf mr-1"></i>Download PDF
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Sustainment alerts (T-048-003) */}
                                                    <div className="border border-gray-200 rounded-lg p-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <h4 className="text-sm font-semibold text-gray-900">4. Sustainment Alerts (Early Warning)</h4>
                                                            <button onClick={handleScanAlerts} disabled={altScanning}
                                                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                                                {altScanning ? <i className="fas fa-spinner fa-spin mr-2"></i> : <i className="fas fa-radar mr-2"></i>}
                                                                Run Scan
                                                            </button>
                                                        </div>
                                                        {!altLoaded && (
                                                            <button onClick={loadSustainmentAlerts} className="text-sm text-rose-600 underline">Load alerts</button>
                                                        )}
                                                        {altList.length > 0 ? (
                                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Indicator</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Severity</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Status</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-200">
                                                                        {altList.map(a => (
                                                                            <tr key={a.id}>
                                                                                <td className="px-3 py-2 text-gray-800">{a.indicator.replace(/_/g, ' ')}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{a.severity}</td>
                                                                                <td className="px-3 py-2 text-gray-600">{a.status}</td>
                                                                                <td className="px-3 py-2 space-x-3">
                                                                                    {a.status === 'OPEN' && (
                                                                                        <>
                                                                                            <button onClick={() => setAltActionTarget({ id: a.id, status: 'ACTIONED', notes: '' })} className="text-xs text-rose-600 underline">Action</button>
                                                                                            <button onClick={() => setAltActionTarget({ id: a.id, status: 'DISMISSED', notes: '' })} className="text-xs text-gray-500 underline">Dismiss</button>
                                                                                        </>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-gray-500">No alerts yet. Run the scan to detect early-warning indicators.</p>
                                                        )}
                                                    </div>
                                                    {altActionTarget && (
                                                        <div className="border border-gray-200 rounded-lg p-4">
                                                            <p className="text-sm text-gray-800 mb-2">{altActionTarget.status === 'ACTIONED' ? 'Action taken' : 'Dismiss'} alert</p>
                                                            <textarea value={altActionNotes} onChange={e => setAltActionNotes(e.target.value)} rows={2}
                                                                placeholder="Notes…"
                                                                className="w-full border border-gray-300 rounded-md p-2 text-sm mb-2" />
                                                            <div className="flex justify-end gap-2">
                                                                <button onClick={() => { setAltActionTarget(null); setAltActionNotes(''); }} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-600">Cancel</button>
                                                                <button onClick={handleActionAlert} disabled={saving}
                                                                    className="px-3 py-1.5 text-sm rounded-md text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">
                                                                    {saving ? <i className="fas fa-spinner fa-spin mr-2"></i> : null} Save
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:ml-3">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
